import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';

// Company-page step 5 (License Preferences), part 1 of 4 (22b-4d step 6a):
// Scoring/Matching settings and the Workflow Steps drag-and-drop order editor -
// the top of the old data-step="5" pane (company-page.html:344-696). The
// remaining parts of this pane (assessment credentials, job-board credentials,
// reports/branding + logo upload) are ported as sibling components in later
// sub-steps; the root controller still owns the combined
// `form.companyAssessmentFlowDetailsForm.$valid` by ANDing every part's
// formStateChange together (see onLicenseScoringFormStateChange).
//
// `dnd-list`/`dnd-draggable` (ng-drag-drop, an AngularJS-only library) is
// replaced with Angular CDK drag-drop, same swap already used for
// upload-req-step-workflow's step-order editor - `stepMoved($index, jobType)`
// (a `splice` that only made sense paired with the old dnd-list's own
// reordering) is dropped in favor of `moveItemInArray` done locally, since
// `workflowStepsOrderToRender` is passed by reference and mutating it here is
// visible to the root controller unchanged.
@Component({
  selector: 'app-company-page-step-license-scoring',
  templateUrl: './company-page-step-license-scoring.component.html'
})
export class CompanyPageStepLicenseScoringComponent implements AfterViewInit, OnDestroy {

  @Input() typeOfCompany: string;
  @Input() companyManagement: any = {};
  @Input() isAssociatedClientUpdateInProgress: boolean;
  @Input() isUserSuperUserOr4dot5Admin: boolean;
  @Input() clientOrBuCreationInProgress: boolean;
  @Input() clientOrBuUpdateInProgress: boolean;
  @Input() isCompanyVendor: boolean;
  @Input() readOnlyForClientOrBU: boolean;
  @Input() preferences: any = {};
  @Input() matchCountList: any[] = [];
  @Input() isAnyWorkflowStepsEnabled: boolean;
  @Input() workflowStepsOrder: any = {};
  @Input() workflowStepsOrderToRender: any = {};
  @Input() companyType: string;

  private static readonly WORKFLOW_STEP_EXCLUDED_KEYS = [
    'fourDotFiveIntellList', 'autoMatch', 'manualMatch',
    'techAssessmentInviteEmailCandidateSetting', 'valueAssessmentInviteEmailCandidateSetting',
    'phoneScreenInviteEmailCandidateSetting', 'interviewInviteEmailCandidateSetting',
    'phoneScreenInviteEmailSetting', 'interviewInviteEmailSetting'
  ];

  @Output() runCandidateScoreCallback = new EventEmitter<void>();
  @Output() toggleAssessmentWorkFlowValuesCallback = new EventEmitter<{ index: number, workflow: any }>();
  @Output() formStateChange = new EventEmitter<any>();

  @ViewChild('companyAssessmentFlowScoringForm') companyAssessmentFlowScoringForm: NgForm;

  private subs: Subscription[] = [];

  ngAfterViewInit() {
    setTimeout(() => this.emitFormState());
    if (this.companyAssessmentFlowScoringForm) {
      this.subs.push(this.companyAssessmentFlowScoringForm.statusChanges.subscribe(() => this.emitFormState()));
    }
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  jobTypes(): string[] {
    return this.workflowStepsOrderToRender ? Object.keys(this.workflowStepsOrderToRender) : [];
  }

  preferenceEntries(): { key: string, value: any }[] {
    if (!this.preferences) {
      return [];
    }
    return Object.keys(this.preferences)
      .filter(key => this.preferences[key] && this.preferences[key].licensePrefDetailsList)
      .map(key => ({ key, value: this.preferences[key] }));
  }

  workflowStepPreferenceEntries(): { key: string, value: any }[] {
    return this.preferenceEntries()
      .filter(entry => CompanyPageStepLicenseScoringComponent.WORKFLOW_STEP_EXCLUDED_KEYS.indexOf(entry.key) === -1);
  }

  showExternalRequisitionSendClientInfo(): boolean {
    return this.companyType === 'StaffingCompany' || this.typeOfCompany === 'Client';
  }

  runCandidateScore() {
    this.runCandidateScoreCallback.emit();
  }

  toggleAssessmentWorkFlowValues(index: number, workflow: any) {
    this.toggleAssessmentWorkFlowValuesCallback.emit({ index, workflow });
  }

  onStepDropped(jobType: string, event: CdkDragDrop<any[]>) {
    if (!this.workflowStepsOrderToRender || !this.workflowStepsOrderToRender[jobType]) {
      return;
    }
    moveItemInArray(this.workflowStepsOrderToRender[jobType], event.previousIndex, event.currentIndex);
  }

  trackByStepId(index: number, step: any) {
    return step && step.id;
  }

  trackByKey(index: number, entry: { key: string, value: any }) {
    return entry.key;
  }

  private emitFormState() {
    const form = this.companyAssessmentFlowScoringForm;
    this.formStateChange.emit({
      $valid: form ? form.valid : true,
      $dirty: form ? form.dirty : false,
      $error: { required: form ? form.invalid : false },
      $setDirty: () => form && form.form.markAsDirty(),
      $setPristine: () => form && form.form.markAsPristine()
    });
  }
}
